import { TRACK_API_URL } from "@/config/constants";
import { hasAnalyticsConsent } from "@/utils/cookieConsent";

export type AnalyticsEventType =
  | "page_view"
  | "whatsapp_click"
  | "calendly_click"
  | "calendly_booked"
  | "resource_download"
  | "resource_register"
  | "contact_submit"
  | "language_toggle"
  | "assistant_open"
  /** A call-to-action link to another page of the site. */
  | "cta_click"
  /** A question opened in an FAQ list. */
  | "faq_open"
  /** A link that leaves the site: Instagram, email, phone… */
  | "outbound_click"
  /** A blog article read to the end. */
  | "article_read"
  /** The advisory intake form was sent; the calendar opens next. */
  | "advisory_form_submit"
  /** A paid resource was added to the cart. */
  | "cart_add"
  /** The cart went to Stripe Checkout. */
  | "checkout_start"
  /** The thank-you page confirmed a paid order. */
  | "purchase_complete"
  /** The Instagram quiz was finished and the email step sent. */
  | "quiz_complete";

const SESSION_KEY = "lucentia_analytics_session";
const VISITOR_KEY = "lucentia_analytics_visitor";

const VALID_EVENTS = new Set<AnalyticsEventType>([
  "page_view",
  "whatsapp_click",
  "calendly_click",
  "calendly_booked",
  "resource_download",
  "resource_register",
  "contact_submit",
  "language_toggle",
  "assistant_open",
  "cta_click",
  "faq_open",
  "outbound_click",
  "article_read",
  "advisory_form_submit",
  "cart_add",
  "checkout_start",
  "purchase_complete",
  "quiz_complete",
]);

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "anonymous";
  }
}

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return getSessionId();
  }
}

function getUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, 200);
  }
  return utm;
}

function normalizePath(path?: string): string {
  const raw = path ?? window.location.pathname;
  const withoutHash = raw.split("#")[0]?.split("?")[0]?.trim() || "/";
  return withoutHash.startsWith("/") ? withoutHash : `/${withoutHash}`;
}

function productionHostname(): string {
  const configured = import.meta.env.VITE_SITE_URL;
  if (configured) {
    try {
      return new URL(configured).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      /* fall through */
    }
  }
  return "lucentiasocial.com";
}

function isProductionSite(): boolean {
  return (
    window.location.hostname.replace(/^www\./, "").toLowerCase() ===
    productionHostname()
  );
}

export function trackEvent(
  eventType: AnalyticsEventType,
  metadata?: Record<string, string | number | boolean | null | undefined>,
): void {
  if (!VALID_EVENTS.has(eventType)) return;
  if (typeof window === "undefined") return;
  if (!isProductionSite()) return;
  if (!hasAnalyticsConsent()) return;

  const path = normalizePath(
    typeof metadata?.path === "string" ? metadata.path : undefined,
  );
  const payload = {
    event_type: eventType,
    path,
    referrer: document.referrer || null,
    session_id: getSessionId(),
    metadata: {
      ...getUtmParams(),
      ...metadata,
      visitor_id: getVisitorId(),
      path,
    },
  };

  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const sent = navigator.sendBeacon(TRACK_API_URL, blob);
      if (sent) return;
    }
  } catch {
    /* fall through to fetch */
  }

  void fetch(TRACK_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    /* fire-and-forget */
  });
}
